/**
 * 分组充值任务：已对某条到账展示做过 popup/read（到账条或合并卡 Receive）的记录。
 * session 中存的是「已关闭一次」的主键：优先 **ledgerId**（多轮与流水对齐），无则 **claimId**（旧数据）。
 * 与 userId 分桶；数据源约定见 groupedRechargeBonusFields.ts。
 */
export function groupedRechargeReadClaimStorageKey(
  userId: string | undefined
): string {
  return `grt_popup_read_claim_ids_${userId ?? "_"}`;
}

/** 将接口可能返回的数字 / 非 string 统一为主键字符串 */
export function coerceIdString(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  const s = String(v).trim();
  return s;
}

/** 单条 popupItem：用于本地去重与接口已读；有 ledger 时仅用 ledger，避免同 claim 多轮误杀。
 * 业务上 ledgerId 唯一；此处仍做数字→字符串、snake_case 兼容，避免接口形态与前端过滤不一致导致「有 popupItems 却永远不入队」。 */
export function popupDismissKeyFromItem(item: {
  ledgerId?: unknown;
  claimId?: unknown;
  ledger_id?: unknown;
  claim_id?: unknown;
}): string | null {
  const lid = coerceIdString(
    item?.ledgerId ?? (item as { ledger_id?: unknown }).ledger_id
  );
  if (lid) return lid;
  const cid = coerceIdString(
    item?.claimId ?? (item as { claim_id?: unknown }).claim_id
  );
  return cid || null;
}

export function isPopupItemDismissedInSet(
  item: {
    ledgerId?: unknown;
    claimId?: unknown;
    ledger_id?: unknown;
    claim_id?: unknown;
  },
  dismissed: Set<string>
): boolean {
  const key = popupDismissKeyFromItem(item);
  return key != null && dismissed.has(key);
}

export function loadGroupedRechargeReadClaimIds(
  userId: string | undefined
): Set<string> {
  try {
    const raw = sessionStorage.getItem(
      groupedRechargeReadClaimStorageKey(userId)
    );
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(
      arr.filter(
        (x): x is string => typeof x === "string" && x.trim().length > 0
      )
    );
  } catch {
    return new Set();
  }
}

export function addGroupedRechargeReadClaimIds(
  userId: string | undefined,
  keys: string[]
): void {
  const set = loadGroupedRechargeReadClaimIds(userId);
  keys.forEach(id => {
    const t = String(id).trim();
    if (t) set.add(t);
  });
  sessionStorage.setItem(
    groupedRechargeReadClaimStorageKey(userId),
    JSON.stringify([...set])
  );
}

// ---- 浮标红叹号：已见任务 ID（localStorage，跨 session 持久化） ----
// key 按 userId 分桶，存储用户「已见过」的 taskGroupId 列表。
// 当前接口任务集合中出现任何未见过的 ID，则显示红叹号。

export function groupedRechargeSeenTaskIdsStorageKey(
  userId: string | undefined
): string {
  return `grt_seen_task_ids_${userId ?? "_"}`;
}

export function loadGroupedRechargeSeenTaskIds(
  userId: string | undefined
): Set<string> {
  try {
    const raw = localStorage.getItem(
      groupedRechargeSeenTaskIdsStorageKey(userId)
    );
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(
      arr.filter(
        (x): x is string => typeof x === "string" && x.trim().length > 0
      )
    );
  } catch {
    return new Set();
  }
}

/** 将当前任务 ID 列表合并写入 localStorage（追加，不覆盖历史） */
export function saveGroupedRechargeSeenTaskIds(
  userId: string | undefined,
  ids: string[]
): void {
  const seen = loadGroupedRechargeSeenTaskIds(userId);
  ids.forEach(id => {
    const t = String(id).trim();
    if (t) seen.add(t);
  });
  try {
    localStorage.setItem(
      groupedRechargeSeenTaskIdsStorageKey(userId),
      JSON.stringify([...seen])
    );
  } catch {
    /* ignore */
  }
}

/** 充值成功 WS 后需在「回到首页」时再拉 config；避免非首页时 loadConfig 抢先返回无 popupItems 的配置覆盖 store */
export const PENDING_GROUPED_CLAIM_CONFIG_RESYNC_SESSION_KEY =
  "gb_pending_grouped_claim_config_resync";

export function markPendingGroupedClaimConfigResync(): void {
  try {
    sessionStorage.setItem(
      PENDING_GROUPED_CLAIM_CONFIG_RESYNC_SESSION_KEY,
      "1"
    );
  } catch {
    /* ignore */
  }
}

export function clearPendingGroupedClaimConfigResync(): void {
  try {
    sessionStorage.removeItem(PENDING_GROUPED_CLAIM_CONFIG_RESYNC_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function peekPendingGroupedClaimConfigResync(): boolean {
  try {
    return (
      sessionStorage.getItem(
        PENDING_GROUPED_CLAIM_CONFIG_RESYNC_SESSION_KEY
      ) === "1"
    );
  } catch {
    return false;
  }
}
